import { useMemo } from 'react';
import {
  PRIMARY_INDUSTRIES,
  type PrimaryIndustrySlug,
} from '@/constants/industries';
import { find } from 'lodash';
import { getReadableCountry } from '@/utilities/markets';
import { SWS_HOST } from '@/constants';
import type { Company } from '../query/fetchPage';
import { ROUTE_STOCKS_MARKET } from '@/constants/routes';
import type { SortBy } from './useSortByOptions';
import { getStocksPath } from '../utils';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import type { MainIndex } from '@/constants/markets';
import { startCase } from 'lodash';
import { useHrefLangMarkets } from '@/hooks/discover/useHrefLangMarkets';
import {
  DEFAULT_INDUSTRY,
  DEFAULT_MARKET,
  DEFAULT_SORT_BY,
} from '../constants';
import useUser from '@/hooks/useUser';

type Index = MainIndex | 'Global';

interface BreadCrumbLinks {
  name: string;
  dispatch?: ReducerAction;
}

const hasCompaniesInIndustry = (
  companies: Company[],
  industry: string
): boolean => {
  const found = companies.find((company) =>
    company.canonicalUrl.includes(industry)
  );
  return found ? true : false;
};

interface Config {
  sortBy: SortBy;
  market: string;
  industry: string;
  index: Index;
}

type getIsIndexableProps = {
  sortBy?: SortBy;
  market?: PrimaryCountryISOElseGlobal;
  industry?: string;
  page: number;
};

export const getIsIndexable = ({
  sortBy,
  market,
  industry,
  page,
}: getIsIndexableProps) => {
  const isDefaultSortBy = sortBy === DEFAULT_SORT_BY;
  const isDefaultMarket = market === DEFAULT_MARKET;
  const isDefaultIndustry = industry === DEFAULT_INDUSTRY;

  return (
    // Only index pages 2+ on our most 'global' list so crawlers can access links.
    page === 1 || (isDefaultSortBy && isDefaultMarket && isDefaultIndustry)
  );
};

export const getTitle = ({ sortBy, market, industry, index }: Config) => {
  const safeIndustry = industry !== 'Any' ? ` ${industry}` : '';
  const safeIndex =
    index !== 'Global' && index !== 'Dow Jones' ? ` (${index})` : '';
  switch (sortBy) {
    case 'market-cap-large':
      return `Largest ${market}${safeIndex}${safeIndustry} Stocks by Market Cap`;
    case 'market-cap-small':
      return `Small Cap ${market}${safeIndex}${safeIndustry} Stocks by Market Cap`;
    case 'top-gainers':
      return `${market}${safeIndex}${safeIndustry} Stocks - Top Gainers Today`;
    case 'biggest-losers':
      return `${market}${safeIndex}${safeIndustry} Stocks - Biggest Losers Today`;
    case 'growth':
      return `${market}${safeIndex} Growth${safeIndustry} Stocks`;
    case 'dividend-yield-high':
      return `${market}${safeIndex}${safeIndustry} Dividend Stocks`;
    default:
      return '';
  }
};

const getSafeIndex = (sortBy, index: Index) => {
  switch (sortBy) {
    case 'market-cap-large':
    case 'market-cap-small':
    case 'growth':
    case 'dividend-yield-high':
      // Global
      if (index === 'Global') return ' from around the world';
      // U.S.
      if (index === 'Dow Jones') return ' that are on the NYSE and NASDAQ';
      // Non global
      return ` that are on the ${index}`;
    case 'top-gainers':
    case 'biggest-losers':
      // Global
      if (index === 'Global') return '';
      // U.S.
      if (index === 'Dow Jones') return ' (NYSE and NASDAQ)';
      // Non global
      return ` (${index})`;
    default:
      return '';
  }
};

const getSafeIndustry = (industry) =>
  industry !== 'Any' ? ` ${industry}` : '';

const getCasing = (word: string, industry) =>
  industry.length > 0 ? startCase(word) : word.toLowerCase();

const getDescription = ({ sortBy, market, industry, index }: Config) => {
  const safeIndustry = getSafeIndustry(industry);
  const safeIndex = getSafeIndex(sortBy, index);
  const safeCaseStocks = getCasing('stocks', safeIndustry);
  switch (sortBy) {
    case 'market-cap-large':
      return `Discover ${market} Large Cap${safeIndustry} Stocks${safeIndex}.`;
    case 'market-cap-small':
      return `Discover ${market} Small Cap${safeIndustry} Stocks${safeIndex}.`;
    case 'top-gainers':
      return `Top Gainers Today. Discover which ${market}${safeIndex}${safeIndustry} ${safeCaseStocks} have gained the most from the previous trading day.`;
    case 'biggest-losers':
      return `Biggest Losers Today. Discover which ${market}${safeIndex}${safeIndustry} ${safeCaseStocks} have dropped the most from the previous trading day.`;
    case 'growth':
      return `Discover ${market}${safeIndustry} Growth Stocks${safeIndex}.${safeIndustry} Growth Stocks have higher earnings or sales expectations compared to other companies.`;
    case 'dividend-yield-high':
      return `Discover ${market}${safeIndustry} High Yield Dividend Stocks${safeIndex}.${safeIndustry} Dividend Stocks can contribute substantially to shareholder returns.`;
    default:
      return '';
  }
};

export const getBlurb = ({ sortBy, market, industry, index }: Config) => {
  const safeIndustry = getSafeIndustry(industry);
  const safeIndex = getSafeIndex(sortBy, index);
  const safeCaseCompanies = getCasing('companies', safeIndustry);
  const safeCaseStocks = getCasing('stocks', safeIndustry);
  switch (sortBy) {
    case 'market-cap-large':
      return `Discover large cap ${market}${safeIndustry} companies${safeIndex}. These${safeIndustry} ${safeCaseCompanies} are organised by Market Cap.`;
    case 'market-cap-small':
      return `Discover small cap ${market}${safeIndustry} companies${safeIndex}. These${safeIndustry} ${safeCaseCompanies} are organised by Market Cap.`;
    case 'top-gainers':
      return `Explore the ${market}${safeIndex} Top Gainers Today. These${safeIndustry} ${safeCaseStocks} have seen the largest rise in Market Cap from the previous trading day.`;
    case 'biggest-losers':
      return `Explore the ${market}${safeIndex} Biggest Losers Today. These${safeIndustry} ${safeCaseStocks} have seen the largest drop in Market Cap from the previous trading day.`;
    case 'growth':
      return `Discover ${market}${safeIndustry} Growth Stocks${safeIndex}. Sorted by forecasted growth, these${safeIndustry} ${safeCaseStocks} have higher than expected earnings or sales growth compared to other companies.`;
    case 'dividend-yield-high':
      return `Discover ${market}${safeIndustry} High Yield Dividend Stocks${safeIndex} and sorted by Dividend Yield. An upper limit of 20% is in place to filter out stocks with potentially unsustainable dividends. All companies with high dividend yields (above 5%) should be researched for sustainability. For more options please click on ‘Advanced Filters’.`;
    default:
      return '';
  }
};

export function useMetaData({
  market,
  industry,
  sortBy = DEFAULT_SORT_BY,
  page,
  hasInfiniteScroll,
  companies,
  routeType,
}: {
  market: PrimaryCountryISOElseGlobal;
  industry: string;
  sortBy?: SortBy;
  page: number;
  hasInfiniteScroll: boolean;
  companies: Company[];
  routeType: string;
}) {
  const { data: user } = useUser();
  const { hrefLangMarkets } = useHrefLangMarkets(routeType, {
    industry,
    sortBy,
  });
  const isIndexable = getIsIndexable({ market, industry, sortBy, page });
  const demonymCountry = getReadableCountry(market);
  const marketMainIndex = getReadableCountry(market, 'mainIndex') as Index;
  const selectedIndustryItem = find(PRIMARY_INDUSTRIES, {
    slug: industry as PrimaryIndustrySlug,
  });
  const readableIndustryLabel = selectedIndustryItem
    ? selectedIndustryItem.label
    : 'Any';

  const url = useMemo(() => {
    return `${SWS_HOST}${getStocksPath({
      market,
      isLoggedIn: user?.loggedIn,
      sortBy,
      industry:
        industry !== DEFAULT_INDUSTRY &&
        hasCompaniesInIndustry(companies, industry)
          ? industry
          : undefined,
    })}`;
  }, [industry, market, sortBy, user?.loggedIn, companies]);

  const title = useMemo(
    () =>
      getTitle({
        sortBy,
        market: demonymCountry,
        industry: readableIndustryLabel,
        index: marketMainIndex,
      }),
    [demonymCountry, sortBy, marketMainIndex, readableIndustryLabel]
  );

  const description = useMemo(
    () =>
      getDescription({
        sortBy,
        market: demonymCountry,
        industry: readableIndustryLabel,
        index: marketMainIndex,
      }),
    [demonymCountry, sortBy, marketMainIndex, readableIndustryLabel]
  );

  const breadCrumbs = useMemo((): BreadCrumbLinks[] => {
    const readableCountry = getReadableCountry(market, 'label');
    return [
      {
        name: readableCountry,
        dispatch: {
          type: ROUTE_STOCKS_MARKET,
          payload: { market: market },
        },
      },
    ];
  }, [market]);

  const blurb = useMemo(
    () =>
      getBlurb({
        sortBy,
        market: demonymCountry,
        industry: readableIndustryLabel,
        index: marketMainIndex,
      }),
    [sortBy, demonymCountry, readableIndustryLabel, marketMainIndex]
  );

  const allowCrawlers = companies.length > 0 && isIndexable;

  return {
    url,
    title,
    blurb,
    breadCrumbs,
    description,
    demonymCountry,
    allowCrawlers,
    paginatedTitle:
      page > 1 && !hasInfiniteScroll ? `Page ${page} - ${title}` : title,
    hrefLangs: page === 1 && allowCrawlers ? hrefLangMarkets : null,
  };
}
